import React, { createContext, useContext, useEffect, useState } from 'react';
import { authLogout, getAuthMe } from '../config';
import { clearStoredSessionToken, getStoredSessionToken, storeSessionToken } from '../auth/session';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    const token = getStoredSessionToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return null;
    }

    try {
      const data = await getAuthMe();
      setUser(data);
      return data;
    } catch {
      clearStoredSessionToken();
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;

    const boot = async () => {
      const token = getStoredSessionToken();
      if (!token) {
        if (active) setLoading(false);
        return;
      }

      try {
        const data = await getAuthMe();
        if (!active) return;
        setUser(data);
      } catch {
        if (!active) return;
        clearStoredSessionToken();
        setUser(null);
      } finally {
        if (active) setLoading(false);
      }
    };

    boot();
    return () => {
      active = false;
    };
  }, []);

  const loginWithSession = async (token, userData = null) => {
    storeSessionToken(token);
    if (userData) {
      setUser(userData);
      setLoading(false);
      return userData;
    }
    return refreshUser();
  };

  const logout = async () => {
    try {
      await authLogout();
    } catch {
      // Ignore logout transport errors and clear the local session anyway.
    } finally {
      clearStoredSessionToken();
      setUser(null);
    }
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    const permissions = user.permissions || [];
    return permissions.includes('*') || permissions.includes(permission);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: Boolean(user),
        hasPermission,
        loginWithSession,
        logout,
        refreshUser,
        setUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
